import { useMemo } from 'react';
import { Icon, Tag, Text, Title } from '@nimbus-ds/components';
import { StoreIcon } from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import {
  LocationResponseDto,
  PickupPointResponseDto,
} from '@tiendanube/common';
import { Stack } from 'commons/components';
import { SelectedZonesInterface } from 'domains/Shipping/components';
import { DeliveryZonesInfo } from 'domains/Shipping/components/shared/ZoneManagement';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { thousandSeparator } from 'domains/Statistics/utils';
import PickupPointItemActions from './PickupPointItemActions';
import PickupPointItemSkeleton from './Skeleton';
import { groupBusinessHours } from './utils';

export interface GroupedBusinessHours {
  days: string[];
  hours: {
    start: string;
    end: string;
  }[];
}

interface PickupPointItemProps {
  pickupPoint: PickupPointResponseDto;
  locations: LocationResponseDto[];
  handleUpdateStatus: (pickupPoint: PickupPointResponseDto) => void;
  handleDelete?: (id: string) => void;
}

function PickupPointItem({
  pickupPoint,
  locations,
  handleUpdateStatus,
  handleDelete,
}: Readonly<PickupPointItemProps>) {
  const t = useTranslationShipping('deliveryMethods.pickupPoints.list');
  const {
    address,
    businessHours,
    cost,
    restrictionArea,
    restrictionType,
    estimateDeliveryTime,
    id,
    isLocation,
    locations: vinculedLocations,
    name,
    status,
    range,
  } = pickupPoint;

  const vinculedLocationsNames = useMemo(
    () =>
      locations
        .filter((location) => vinculedLocations.includes(location.id))
        .map(({ name }) => name),
    [locations, vinculedLocations],
  );

  const deliveryZones = useMemo((): SelectedZonesInterface[] => {
    if (!restrictionArea || restrictionType === 'country') {
      return [];
    }
    return restrictionArea;
  }, [restrictionArea, restrictionType]);

  const estimatedTimeText = useMemo(
    () =>
      estimateDeliveryTime.maxDays === 0
        ? t('estimatedDeliveryTime.withoutDays')
        : estimateDeliveryTime.minDays === 0
        ? t('estimatedDeliveryTime.withJustMaxDay', {
            maxDays: estimateDeliveryTime.maxDays,
          })
        : t('estimatedDeliveryTime.withDays', {
            minDays: estimateDeliveryTime.minDays,
            maxDays: estimateDeliveryTime.maxDays,
          }),
    [estimateDeliveryTime, t],
  );

  const costText = useMemo(() => {
    const costKey = cost.type === 'to_agree' ? 'toAgree' : 'free';
    return cost.type === 'fixed'
      ? t('cost.fixed', {
          value: thousandSeparator(parseFloat(cost.value), false),
        })
      : t(`cost.${costKey}`);
  }, [cost, t]);

  const formattedBusinessHours = useMemo(() => {
    const groupedHours = groupBusinessHours(businessHours ?? []);
    const hoursByDay: Record<string, { start: string; end: string }[]> = {};

    businessHours?.forEach(({ day, start, end }) => {
      if (!hoursByDay[day]) {
        hoursByDay[day] = [];
      }
      hoursByDay[day].push({ start, end });
    });

    const formatSingleDayHours = (
      day: string,
      hours: { start: string; end: string }[],
    ) => {
      const dayName = t(`daysOfWeek.${day}`);
      const timeRanges = hours.map((slot) => `${slot.start} a ${slot.end}`);
      return `${dayName} de ${timeRanges.join(
        ' ' + t('multipleDays.and') + ' ',
      )}`;
    };

    const formatRangeDaysHours = (
      startDay: string,
      endDay: string,
      hours: { start: string; end: string }[],
    ) => {
      const timeRanges = hours.map((slot) => `${slot.start} a ${slot.end}`);
      const startDayTranslated = t(`daysOfWeek.${startDay}`);
      const endDayTranslated = t(`daysOfWeek.${endDay}`);
      const timeRangesJoined = timeRanges.join(
        ' ' + t('multipleDays.and') + ' ',
      );
      return `De ${startDayTranslated} a ${endDayTranslated} de ${timeRangesJoined}`;
    };

    const formattedBusinessHours: string[] = [];

    groupedHours.forEach((group) => {
      if (group.days.length === 1) {
        formattedBusinessHours.push(
          formatSingleDayHours(group.days[0], group.hours),
        );
        return;
      }

      formattedBusinessHours.push(
        formatRangeDaysHours(
          group.days[0],
          group.days[group.days.length - 1],
          group.hours,
        ),
      );
    });

    return formattedBusinessHours;
  }, [businessHours, t]);

  return (
    <DataList.Row id={id}>
      <Stack justify="space-between">
        <Stack column align="flex-start">
          <Stack spacing="tight">
            <Title as="h6">{isLocation ? locations[0].name : name}</Title>
            <Tag appearance={status ? 'success' : 'neutral'}>
              {t(status ? 'status.active' : 'status.inactive')}
            </Tag>
          </Stack>
          <Stack column align="flex-start" spacing="none">
            <Text>{isLocation ? locations[0].address : address}</Text>
            <Text>{formattedBusinessHours.join(' - ')}</Text>
            <Text>
              {costText} - {estimatedTimeText}
            </Text>
            {!!range && (
              <Text>
                {t('range', {
                  range: thousandSeparator(parseInt(range), true),
                })}
              </Text>
            )}
            {deliveryZones && deliveryZones.length > 0 && (
              <DeliveryZonesInfo deliveryZones={deliveryZones} />
            )}
          </Stack>
          <Stack align="flex-start" spacing="tight" wrap>
            {!isLocation &&
              vinculedLocationsNames.length > 0 &&
              vinculedLocationsNames.map((location) => (
                <Tag key={location} appearance="neutral">
                  <Icon source={<StoreIcon />} />
                  {location}
                </Tag>
              ))}
          </Stack>
        </Stack>
        <PickupPointItemActions
          handleUpdateStatus={handleUpdateStatus}
          handleDelete={handleDelete}
          pickupPoint={pickupPoint}
        />
      </Stack>
    </DataList.Row>
  );
}

PickupPointItem.Skeleton = PickupPointItemSkeleton;

export default PickupPointItem;
